// R9 (pure part) — interleaving. On a narrow screen the reader has no room for
// two columns, so the original is woven INTO the retelling: a run of retold
// blocks, then the stretch of the original that run corresponds to, then the
// next run, and so on. This module computes that weave from the same anchor
// sidecar the desktop panes sync on (SPEC "Sync anchors": alignment is COMPUTED
// at render time, never a stored merged document).
//
// Everything here is a pure, total function of its inputs — no DOM, no
// measurement, no throwing (functional-programming policy). Degenerate input
// (no anchors, a stale `blockId`, a non-monotonic or out-of-range offset)
// degrades to a coarser weave rather than failing: the worst case is one
// segment holding the whole chapter, which is exactly what the two-pane path
// does with an empty anchor list.
//
// Anchor conventions are shared with `anchor-geometry`/`use-hard-align`: the
// document-start and document-end sentinel pairs stay IMPLICIT, and an anchor
// that doesn't strictly advance past the previous kept one in BOTH documents is
// dropped.

import type { CorpusAnchorPair } from "@bindery/shared/corpus/anchors";
import type {
  DocumentBlock,
  DocumentModel,
} from "@bindery/shared/corpus/document";

/** One interleaved beat: a run of retelling blocks and the original it retells. */
export interface InterleavedSegment {
  /** Retelling blocks, in document order. */
  retellingBlocks: readonly DocumentBlock[];
  /** The original's blocks over `original`, in document order. */
  originalBlocks: readonly DocumentBlock[];
  /**
   * Char span `[start, end)` into `original.text`. Block-aligned: `start` is
   * always some block's start, so a stretch never opens mid-sentence. Spans are
   * contiguous — each segment's `end` is the next segment's `start` — and
   * together they cover the whole original.
   */
  original: { start: number; end: number };
}

/** Where one segment begins in both documents. */
interface Cut {
  /** Index into `retelling.blocks`. */
  retellingIndex: number;
  /** Block-aligned char offset into `original.text`. */
  originalStart: number;
}

/** The implicit document-start sentinel — always the first cut. */
const DOCUMENT_START: Cut = { retellingIndex: 0, originalStart: 0 };

/**
 * Snap an original char offset to the start of the block that owns it — or, if
 * it lands in the whitespace between blocks, to the next block's start. `null`
 * when the offset is past the last block, so it can't open a stretch. Same
 * containing-block rule `use-hard-align`'s `originalBlockEl` snaps with.
 */
function snapToBlockStart(
  blocks: readonly DocumentBlock[],
  offset: number,
): number | null {
  return blocks.find((block) => offset < block.end)?.start ?? null;
}

/** Retelling block id → its index. Blocks without an id (the heading) are skipped. */
function indexByBlockId(blocks: readonly DocumentBlock[]): Map<string, number> {
  return new Map(
    blocks.flatMap((block, index) =>
      block.id === undefined ? [] : [[block.id, index] as const],
    ),
  );
}

/**
 * Fold the anchors into the cuts that survive: an anchor is kept only when it
 * resolves in both documents AND strictly advances past the previous kept cut
 * on both sides. Dropping the rest is what makes the weave total — a stale
 * block id, two anchors inside one paragraph, or a backwards pair merge into
 * the surrounding segment instead of producing an empty or inverted one.
 */
function resolveCuts(
  retelling: DocumentModel,
  original: DocumentModel,
  anchors: readonly CorpusAnchorPair[],
): Cut[] {
  const indexOf = indexByBlockId(retelling.blocks);
  // "Advances past the lead segment" is measured against the FIRST BLOCK's
  // start, not offset 0: an original whose first block begins past 0 (leading
  // matter the flattener emitted no block for) would otherwise let an anchor
  // inside that first block snap to its start, count as advancing, and open a
  // second segment — leaving the lead one holding no original block at all.
  // The lead segment's span still opens at 0, so that text stays covered.
  const lead: Cut = {
    retellingIndex: DOCUMENT_START.retellingIndex,
    originalStart: original.blocks[0]?.start ?? DOCUMENT_START.originalStart,
  };
  return anchors.reduce<Cut[]>((kept, pair) => {
    const previous = kept[kept.length - 1] ?? lead;
    const retellingIndex = indexOf.get(pair.retelling.blockId);
    const originalStart = snapToBlockStart(
      original.blocks,
      pair.originalOffset,
    );
    if (retellingIndex === undefined || originalStart === null) return kept;
    if (
      retellingIndex <= previous.retellingIndex ||
      originalStart <= previous.originalStart
    ) {
      return kept;
    }
    return [...kept, { retellingIndex, originalStart }];
  }, []);
}

/**
 * A stretch that is nothing but a heading. The column renders every stretch as
 * a disclosure clamped to three lines, so a one-heading stretch is identical
 * open and closed but for the chevron — tapping it looks broken. (Not
 * hypothetical: the first stretch of both Odyssey I and Heidi I was exactly
 * this, the original's own chapter title standing alone.)
 */
function isHeadingOnly(segment: InterleavedSegment): boolean {
  return (
    segment.originalBlocks.length > 0 &&
    segment.originalBlocks.every((block) => block.kind === "heading")
  );
}

/**
 * Concatenate two ADJACENT segments, `first` before `second`. Both sides stay
 * in document order and the span runs from the first's start to the second's
 * end — which is exactly their union, since adjacent spans already meet — so
 * every partition guarantee below survives a merge untouched.
 */
function mergeSegments(
  first: InterleavedSegment,
  second: InterleavedSegment,
): InterleavedSegment {
  return {
    retellingBlocks: [...first.retellingBlocks, ...second.retellingBlocks],
    originalBlocks: [...first.originalBlocks, ...second.originalBlocks],
    original: { start: first.original.start, end: second.original.end },
  };
}

/**
 * Fold every heading-only stretch into a neighbour, so its heading rides along
 * with real prose instead of standing as a disclosure of its own. Right to
 * left, so a run of them collapses in one pass: by the time a segment is
 * examined its successor has already absorbed whatever followed it.
 *
 * The LAST segment has no successor to fold into, so it folds BACKWARDS
 * instead — a chapter whose original ends on the next chapter's heading is the
 * same broken disclosure at the other end of the column. A lone segment stays
 * put either way: folding cannot manufacture prose that isn't there, and
 * dropping the stretch would lose its text from the render.
 */
function foldHeadingOnlyStretches(
  segments: readonly InterleavedSegment[],
): InterleavedSegment[] {
  const folded = segments.reduceRight<InterleavedSegment[]>(
    (rest, segment) =>
      rest[0] && isHeadingOnly(segment)
        ? [mergeSegments(segment, rest[0]), ...rest.slice(1)]
        : [segment, ...rest],
    [],
  );
  const last = folded[folded.length - 1];
  const previous = folded[folded.length - 2];
  return last && previous && isHeadingOnly(last)
    ? [...folded.slice(0, -2), mergeSegments(previous, last)]
    : folded;
}

/**
 * Weave a chapter into interleaved segments.
 *
 * Guarantees, for any input:
 * - every retelling block appears in exactly one segment, in document order;
 * - so does every original block — including one sitting at or past
 *   `text.length`, which the last segment claims even though the spans stop
 *   there, because a dropped block is text missing from the render;
 * - the `original` spans are contiguous and ascending, covering the whole text;
 * - every segment holds at least one block of each side (when that side has
 *   any), so no beat renders an orphan run;
 * - no segment's stretch is a heading on its own, unless the whole original is
 *   headings and there is nothing to fold into.
 */
export function interleaveSegments(
  retelling: DocumentModel,
  original: DocumentModel,
  anchors: readonly CorpusAnchorPair[],
): InterleavedSegment[] {
  const cuts = [DOCUMENT_START, ...resolveCuts(retelling, original, anchors)];
  // The implicit document-END sentinel. `Math.max` keeps the last span ascending
  // even if a block claimed to start past the end of the text it indexes into.
  const documentEnd = Math.max(
    original.text.length,
    cuts[cuts.length - 1]!.originalStart,
  );

  const segments = cuts.map((cut, i) => {
    const next = cuts[i + 1];
    const retellingEnd = next?.retellingIndex ?? retelling.blocks.length;
    const originalEnd = next?.originalStart ?? documentEnd;
    // The LAST segment claims every remaining block, however it is offset:
    // a trailing zero-length block sits at exactly `text.length`, outside the
    // half-open span, so bounding the filter by `originalEnd` would drop it
    // from every segment and silently lose its text from the render.
    const blockEnd = next?.originalStart ?? Infinity;
    return {
      retellingBlocks: retelling.blocks.slice(cut.retellingIndex, retellingEnd),
      originalBlocks: original.blocks.filter(
        (block) => block.start >= cut.originalStart && block.start < blockEnd,
      ),
      original: { start: cut.originalStart, end: originalEnd },
    };
  });

  return foldHeadingOnlyStretches(segments);
}
